import {IGame, IPollContentBlock} from "@webng-types/write-model";
import React, {useState, useEffect, useCallback} from "react";
import {
  PollQueryResult,
  useCastVote,
  useFetchPoll,
  useSaveAnswer,
} from "../polls-api/usePolls";
import { CheckCircle } from "react-feather";
import { useTranslation } from "react-i18next";
// import { generateRandomId } from "@webng/uniqueid-generator";
import { CircleLoader } from "@webng/icons-common";
import { IEvent } from "@webng-types/write-model";
import { useQueryClient } from "@tanstack/react-query";
import { useNiceEndTime } from "@webng/react-app-common";
import {useTrackingFunction} from "../LiveblogRenderContext";

export type PollContentBlockProps = {
  block: IPollContentBlock;
  isPreview?: boolean;
  event: IEvent;
  game: IGame;
};

export function PollContentBlockView({
  block,
  isPreview,
  event,
  game
}: PollContentBlockProps) {
  const { t } = useTranslation("liveblog");
  const trackingFunction = useTrackingFunction()

  const [state, error, isLoading, refetch] = useFetchPoll({
    pollId: block.local_id ?? "",
    organizationId: game.owner?._id!
  });

  const [isVoteLoading, isVoteError, castVote] = useCastVote({
    pollId: state?.poll.pollId ?? block.local_id ?? "",
    organizationId: game.owner?._id!
  });

  const castVoteTracked = useCallback((answerId: string) => {
    trackingFunction({t: 'a_vot', e: event.local_id})
    castVote(answerId, {
      onSettled: () => setLoadingAnswer(undefined)
    })
  }, [castVote, trackingFunction, event.local_id])

  const [{ answerId: savedAnswer }] = useSaveAnswer(
    state?.poll.pollId ?? block.local_id ?? ""
  );

  const [loadingAnswer, setLoadingAnswer] = useState<string | undefined>(
    undefined
  );

  const [clickedShowResults, setClickedShowResulsts] = useState(false);

  const queryClient = useQueryClient();

  const loading = isLoading || !state;

  const closed = !(error || loading)
    ? Date.now() >= new Date(state.poll?.expiration).getTime()
    : false;

  const expirationLabel = useFormatExpirationDate(state, block);

  useEffect(() => {
    queryClient.setQueryData<PollQueryResult | undefined>(
      ["pollQuery", state?.poll.pollId ?? block.local_id ?? ""],
      (oldData: PollQueryResult | undefined) => {
        return oldData
          ? {
              ...oldData,
              poll: {
                ...oldData.poll,
                question: block.question ?? oldData.poll.question,
                expiration: new Date((block.closes_at ?? 0) * 1000),
              },
              answers:
                block.answers?.map((answer) => {
                  return {
                    pollId: oldData.poll.pollId,
                    answerId: answer.local_id,
                    text: answer.text ?? "",
                    count:
                      oldData.answers.find(
                        (a) => a.answerId === answer.local_id
                      )?.count ?? 0,
                  };
                }) ?? oldData.answers,
            }
          : undefined;
      }
    );
  }, [refetch, event, block, queryClient, state?.poll.pollId]);

  const showResults =
    closed ||
    savedAnswer !== undefined ||
    (!(error || loading) && state.vote !== undefined) ||
    clickedShowResults;

  const hasVoted =
    savedAnswer !== undefined ||
    (!(error || loading) && state.vote !== undefined);

  let totalVotes = !(error || loading)
    ? state.answers.map((answer) => answer.count).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <>
      <div
        className={`tik4-poll ${
          loading ? "tik4-poll--loading tik4-loading-content" : ""
        }`}
      >
        {loading && (
          <>
            <div className="tik4-poll__q">
              <div className="tik4-poll__q__c">{block.question}</div>
            </div>
            <div className="tik4-poll__c">
              <div className="tik4-poll__ol">
                {block.answers?.map((answer) => (
                  <div key={answer.local_id} className="tik4-poll__ol__oi">
                    <button
                      className="tik4-button tik4-button--outline-dark tik4-poll__ob"
                      disabled={true}
                    >
                      <div className="tik4-poll__ob__t">{answer.text}</div>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="tik4-poll__f">
              <div className="tik4-poll__f__vc"></div>
              <div className="tik4-poll__f__d">&middot;</div>
            </div>
          </>
        )}

        {error && (
          <>
            <div className="tik-poll__e">{t("liveblog.poll.error")}</div>
          </>
        )}

        {!(error || loading) && (
          <>
            <div className="tik4-poll__q">
              <div className="tik4-poll__q__c">
                {state.poll?.question ?? block.question ?? ""}
              </div>
            </div>
            <div className="tik4-poll__c">
              {!showResults && (
                <>
                  <div className="tik4-poll__ol">
                    {state.answers
                      .filter((answer) => answer.deleted_at === undefined)
                      .map((answer) => (
                        <div
                          key={answer.answerId}
                          className="tik4-poll__ol__oi"
                        >
                          <button
                            disabled={
                              isVoteLoading ||
                              savedAnswer !== undefined ||
                              loadingAnswer !== undefined
                            }
                            className="tik4-button tik4-button--outline-dark tik4-poll__ob"
                            onClick={() => {
                              setLoadingAnswer(answer.answerId);
                              if (
                                isVoteLoading ||
                                savedAnswer !== undefined ||
                                loadingAnswer !== undefined
                              )
                                return;
                              else
                                castVoteTracked(answer.answerId);
                            }}
                          >
                            {loadingAnswer === answer.answerId && (
                              <CircleLoader className="tik4-icon tik4-spin tik4-poll__ob__l" />
                            )}
                            <div className="tik4-poll__ob__t">
                              {answer.text}
                            </div>
                          </button>
                        </div>
                      ))}
                  </div>
                </>
              )}

              {showResults && (
                <>
                  <div className="tik4-poll__rl">
                    {state.answers
                      .filter((answer) => answer.deleted_at === undefined)
                      .map((answer) => (
                        <div key={answer.answerId} className="tik4-poll__ri">
                          <div
                            className="tik4-poll__rp"
                            style={{
                              width: `${
                                totalVotes > 0
                                  ? Math.round(
                                      (answer.count / totalVotes) * 100
                                    )
                                  : 0
                              }%`,
                            }}
                          ></div>
                          <div className="tik4-poll__rc">
                            <div className="tik4-poll__rc__t">
                              <div className="tik4-poll__rc__t__c">
                                {answer.text}
                              </div>
                              {(state.vote === answer.answerId ||
                                savedAnswer === answer.answerId) && (
                                <CheckCircle className="tik4-icon tik4-poll__rc__t__i" />
                              )}
                            </div>
                            <div className="tik4-poll__rc__v">
                              {totalVotes > 0
                                ? Math.round((answer.count / totalVotes) * 100)
                                : 0}
                              %
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </div>
            {isVoteError && (
              <div className="tik4-feedback tik4-poll__feedback">
                <div className="tik4-feedback__text">
                  {t("liveblog.poll.vote_error")}
                </div>
              </div>
            )}
            <div className="tik4-poll__f">
              <div className="tik4-poll__f__vc">
                {t("liveblog.poll.footer.vote_count", { count: totalVotes })}
              </div>
              <div className="tik4-poll__f__d">&middot;</div>
              {/* TODO:Max! relative date (3 days, 2 hours, 5 minutes) */}
              <div className="tik4-poll__f__ca">
                <span className="tik4-poll__f__ca__d">{expirationLabel}</span>
              </div>

              {isPreview && !showResults && (
                <>
                  <div className="tik4-poll__f__d">&middot;</div>
                  <button
                    onClick={() => setClickedShowResulsts(true)}
                    className="tik4-link"
                  >
                    {t("liveblog.poll.footer.show_results")}
                  </button>
                </>
              )}
              {isPreview && showResults && !closed && !hasVoted && (
                <>
                  <div className="tik4-poll__f__d">&middot;</div>
                  <button
                    className="tik4-link"
                    onClick={() => setClickedShowResulsts(false)}
                  >
                    {t("liveblog.poll.footer.hide_results")}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function useFormatExpirationDate(state: PollQueryResult | undefined, block: IPollContentBlock): string | undefined {
  const { t } = useTranslation("liveblog");
  const expiration = useNiceEndTime(state ? new Date(state.poll.expiration).getTime() : block.closes_at);

  const date = state?.poll.expiration ?? block.closes_at;

  if(!date) return undefined

  if(Date.now() >= new Date(date).getTime()) return t("liveblog.poll.footer.closed");

  const ends = t("liveblog.poll.footer.ends");

  return expiration ? `${ends} ${expiration}` : undefined;
}
